import Backspace from "@mui/icons-material/Backspace";
import ChangeCircleOutlined from "@mui/icons-material/ChangeCircleOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import East from "@mui/icons-material/East";
import Edit from "@mui/icons-material/Edit";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
	Avatar,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Menu,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CalendarIcon } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { generalHighlightNurseId } from "../../contexts/NurseHighlight";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { generalHighlightShift } from "../../contexts/ShiftHighlight";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useDepartmentNurseTypes } from "../../hooks/useDepartmentNurseTypes";
import { useErrorSnackbar } from "../../hooks/useErrorSnackbar";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { useHandleRequest } from "../../hooks/useHandleRequest";
import { usePrepareNurseShift } from "../../hooks/usePrepareNurseShift";
import { useRosterLookup } from "../../hooks/useRosterLookup";
import { useScheduleDayOf } from "../../hooks/useScheduleDayOf";
import { useShiftTimes } from "../../hooks/useShiftTimes";
import { SEVERITY } from "../../theme/tokens";
import { showAgencyPostingResult } from "../../utils/agencyWarnings";
import { trackEvent } from "../../utils/analytics";
import type { StaffingIssue, Suggestion } from "../../utils/autoStaffing";
import type { NurseShiftDraft } from "../../utils/buildNurseShift";
import { freeLabelColor } from "../../utils/categoricalColors";
import { findScheduleSlot, scheduleSlotDay } from "../../utils/departmentWeeks";
import {
	type AgencyPostingFailure,
	type ChangesSource,
	DepartmentNames,
	IssuePriority,
	NotificationType,
	type Nurse,
	NurseFullRole,
	type NurseInShift,
	NurseInShiftCreationSource,
	type NurseRole,
	type OpenShiftRequest,
	type ReplaceShiftRequest,
	RequestResponsesStatusOptions,
	type ScheduleData,
	ShiftType,
	SuggestionAction,
} from "../../utils/models";
import axiosInstance from "../../utils/networkWrapper";
import { getRandomInt, hashString } from "../../utils/utilFunctions";
import { isAgencyNurse, useNurseWarnings, WarningDialog } from "../../utils/warningDialogs/WarningDialog";
import { AddNurse } from "../AddNurse/AddNurse";
import { MoveNursePanel } from "../approvalDialogs/MoveNursePanel";
import { NurseRemovalDialog } from "../approvalDialogs/NurseRemovalDialog";
import { NormalizedRequestApprovalDialog } from "../approvalDialogs/RequestsApproval/NormalizedRequestApprovalDialog";
import { ErrorBoundary } from "../ErrorBoundary";
import { HeaderButton } from "../headerButton";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import { MessageWorkingFilterEnum } from "../StaffCommunication/util/Broadcast/MessageFilter";
import { getShiftPickupMessage, NewBroadcastMessageModal } from "../StaffCommunication/util/Broadcast/NewBroadcastMessageModal";
import { getRecommendationTitle } from "../utils/Recommendations-utils";

interface RecommendationProps {
	rec: StaffingIssue;
	/** The weeks and staff the suggestions' positions (`weekIndex`, `shiftNo`, `dayNo`) point into: the ones the engine ran on. */
	schedule: ScheduleData;
	index: string | number;
	implementedChanges: Record<string, boolean>;
	setImplementedChanges: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
	weekIndex: number;
	startingDate: DateTime;
	onRemoveRecommendation: (index: string | number, suggestionIndex: number, isCancel: boolean, nurse_id?: number) => void;
	isRequestedTab: boolean;
	isTitleOnly?: boolean;
	isAutoScheduler?: boolean;
	requestType?: NotificationType;
	request?: OpenShiftRequest | ReplaceShiftRequest;
	onUpdateRequest?: (updateFunc: (request: OpenShiftRequest) => OpenShiftRequest) => void;
	assignments?: string[];
	relevantRoles?: NurseRole[];
	bonus?: number;
	onTimeChange?: (request: OpenShiftRequest | ReplaceShiftRequest, newStartTime: DateTime, newEndTime: DateTime, source: ChangesSource) => Promise<void>;
}

export const Recommendation: React.FC<RecommendationProps> = ({
	rec,
	schedule,
	index,
	implementedChanges,
	weekIndex,
	startingDate,
	onRemoveRecommendation,
	isRequestedTab = false,
	isTitleOnly = false,
	isAutoScheduler,
	requestType,
	request,
	onUpdateRequest,
	assignments,
	relevantRoles,
	bonus,
	onTimeChange,
}) => {
	const { t, i18n } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);
	const [isApproved, setIsApproved] = useState(false);
	const [deleteRequestConfirmation, setDeleteRequestConfirmation] = useState<boolean>(false);
	const { showSnackbar } = useSnackbar();
	const [imageUrls, setImageUrls] = useState<{ [nurseId: number]: string }>({});
	// Applying a suggestion to the old list leaves with the in-browser engine; names move with the roster (Phase 6).
	const { handleSuggestion } = useHospitalData();
	const { chosenDepartment } = useChosenDepartment();
	const { getNurseFormattedName } = useRosterLookup(chosenDepartment.department_id);
	const { isSnf, isHospitalView } = useDepartmentFlags();
	const showErrorSnackbar = useErrorSnackbar();
	const departmentNurseTypes = useDepartmentNurseTypes(chosenDepartment.department_id);
	const { getShiftTimes } = useShiftTimes();
	const scheduleDayOf = useScheduleDayOf();
	const prepareNurseShift = usePrepareNurseShift();
	// The shift an approval adds for `nurseId`; undefined when they aren't on the schedule's roster.
	const nurseShiftOf = (
		nurseId: number,
		draft: Omit<NurseShiftDraft, "nurse" | "addedFromActual" | "nurseInShiftId" | "isDraftMode">,
	): NurseInShift | undefined => {
		const nurse = schedule.nurses.find((rosterNurse) => rosterNurse.id === nurseId);
		return nurse && prepareNurseShift({ ...draft, nurse, addedFromActual: false, nurseInShiftId: undefined, isDraftMode: false });
	};
	// Hospital demos pick by the request's id, so a card keeps its department instead of a new random
	// one on every render. Other departments keep the random pick.
	const assignment: string | undefined =
		isHospitalView && request && assignments?.length
			? assignments[Math.abs(hashString(String(request.id))) % assignments.length]
			: assignments?.[getRandomInt(0, assignments.length - 1)];
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [editingNeededNurses, setEditingNeededNurses] = useState<number>(1);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isAddApprovalDialogOpen, setIsAddApprovalDialogOpen] = useState(false);
	const [isReplaceApprovalDialogOpen, setIsReplaceApprovalDialogOpen] = useState(false);
	const [isMatrixOpen, setIsMatrixOpen] = useState<boolean>(false);
	const [isRequestApprovalDialogOpen, setIsRequestApprovalDialogOpen] = useState(false);
	const [isRemoveApprovalDialogOpen, setIsRemoveApprovalDialogOpen] = useState(false);
	const { setStaffRequests, staffRequests } = useRequestsContext();
	const [warningState, checkWarnings, handleAgencyConfirm, handleOvertimeConfirm, handleWarningCancel] = useNurseWarnings(() => {
		executeAcceptSuggestion(pendingSuggestionIndex);
	}, chosenDepartment.longShiftDuration);
	const [pendingSuggestionIndex, setPendingSuggestionIndex] = useState<number>(-1);
	const { addNurse, removeNurse, moveNurse, isLoading: isLoadingNurseChange } = useHandleNurseChange();
	const { isLoading, replaceRequest } = useHandleRequest();
	const [showBroadcastMessagePopup, setShowBroadcastMessagePopup] = useState(false);

	const toggleShowBroadcastMessagePopup = useCallback(() => {
		setShowBroadcastMessagePopup((prevState) => !prevState);
	}, []);

	const isLoadingRequest = isLoading.replaceRequest || isLoadingNurseChange.moveNurse || isLoadingNurseChange.addNurse || isLoadingNurseChange.removeNurse;
	// Set initial value for needed nurses text
	useEffect(() => {
		const neededNursesMatch = rec.title[4]?.text?.match(/\d+/);
		const currentNeededNurses = neededNursesMatch ? Number.parseInt(neededNursesMatch[0]) : 1;
		setEditingNeededNurses(currentNeededNurses);
	}, []);

	const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		const neededNursesMatch = rec.title[4]?.text?.match(/\d+/);
		const currentNeededNurses = neededNursesMatch ? Number.parseInt(neededNursesMatch[0]) : 1;
		setEditingNeededNurses(currentNeededNurses);
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const validateScheduledDeactivationDate = (recId: number) => {
		const nurseId = rec.suggestions[recId].nurseId;

		const nurse = schedule.nurses.find((n) => n.id === nurseId);

		if (nurse?.scheduledDeactivationDate && rec.shiftTimes?.start && nurse.scheduledDeactivationDate < rec.shiftTimes.start) {
			showErrorSnackbar(t("nurse.deactivated.on.shift"), {
				action: "nurse is scheduled to be deactivated on this shift",
				nurse_id: nurseId,
			});

			return false;
		}

		return true;
	};

	const handleSave = async () => {
		try {
			const { data } = await axiosInstance.patch<{ message: string; agency_posting_failures?: AgencyPostingFailure[] }>(
				`/staffing/open-shift/${rec.notificationId}/needed-nurses`,
				{
					needed_nurses: editingNeededNurses,
				},
			);
			onUpdateRequest?.((request: OpenShiftRequest) => ({
				...request,
				needed_nurses: editingNeededNurses,
			}));
			showAgencyPostingResult(showSnackbar, t("success.needed.nurses.updated"), t, data?.agency_posting_failures);
			handleClose();
		} catch (error) {
			showErrorSnackbar(
				t("error.update.needed.nurses"),
				{
					action: "updated needed nurses failed",
					shift_id: rec.notificationId,
				},
				error,
			);
		}
	};

	const onDeleteReplacementRequest = (): void => {
		setDeleteRequestConfirmation(true);
	};

	const handleDelete = async () => {
		setIsDeleting(true);
		try {
			const response = await axiosInstance.delete<{ message: string; agency_posting_failures?: AgencyPostingFailure[] }>(
				`/staffing/open-shift/${rec.notificationId}`,
				{
					params: {
						department_id: chosenDepartment?.department_id,
					},
				},
			);

			setStaffRequests(staffRequests.filter((r) => r.id !== rec.notificationId));

			if (isRequestedTab) {
				trackEvent("Staffing - request deleted", {
					source: "recommendation",
					request_id: rec.notificationId,
					date: rec.shiftTimes?.date?.toFormat("MM/DD/yyyy"),
				});
			} else {
				trackEvent("Staffing - recommendation deleted", {
					source: "recommendation",
					recommendation_id: rec.notificationId,
					date:
						rec.suggestions?.length > 0 &&
						scheduleSlotDay(schedule.weeks, { weekIndex, shiftIndex: rec.suggestions[0].shiftNo, dayIndex: rec.suggestions[0].dayNo }).date.toFormat(
							"MM/DD/yyyy",
						),
				});
			}
			showAgencyPostingResult(showSnackbar, t("success.deleted.open.shift"), t, response.data.agency_posting_failures);
			onRemoveRecommendation(rec.notificationId || "", -1, true);
		} catch (error) {
			showErrorSnackbar(
				t("error.delete.open.shift"),
				{
					action: "deleted open shift failed",
					shift_id: rec.notificationId,
				},
				error,
			);
		} finally {
			setIsDeleting(false);
		}
	};

	useEffect(() => {
		getImage();
	}, [rec.suggestions]);

	const getImageByNurseId = async (nurseId: number) => {
		const cachedImage = localStorage.getItem(`profile_image_${nurseId}`);
		if (cachedImage) {
			setImageUrls((prev) => {
				return { ...prev, [nurseId]: cachedImage };
			});
			return;
		}
		const nurse = schedule.nurses.find((n) => n.id === nurseId);
		if (nurse?.profileImage && nurse.profileImage.length > 0) {
			const response = await axiosInstance.get(`/images/get_profile_image_base64/${nurseId}`);
			try {
				localStorage.setItem(`profile_image_${nurseId}`, `data:image/jpeg;base64,${response.data}`);
			} catch (e) {
				showErrorSnackbar(
					"Failed to save profile image to local storage",
					{
						action: "failed to save profile image to local storage",
						nurse_id: nurseId,
					},
					e,
				);
			}
			setImageUrls((prev) => {
				return { ...prev, [nurseId]: response.data };
			});
		}
	};

	const getImage = async () => {
		rec.suggestions.forEach(async (suggestion) => {
			if (suggestion.nurseId) {
				getImageByNurseId(suggestion.nurseId);
			}
			if (suggestion.otherNurseId) {
				getImageByNurseId(suggestion.otherNurseId);
			}
		});
	};

	const approveSuggestionAsync = async (nurse: Nurse, dayNo: number, shiftNo: number, idx: number, weekIndex: number) => {
		const slotDay = scheduleSlotDay(schedule.weeks, { weekIndex, shiftIndex: shiftNo, dayIndex: dayNo });
		let nurseToAdd = nurseShiftOf(nurse.id, { ...slotDay, location: undefined, requestId: request?.id, shiftTimes: undefined, shiftBonus: bonus });
		if (!nurseToAdd) {
			showErrorSnackbar(t("error.approve.request"), { nurse_id: nurse.id, request_id: request?.id, reason: "nurse not on the department roster" });
			throw new Error(`Nurse ${nurse.id} is not on the department roster`);
		}

		const nurseIdToCompare = rec?.suggestions[idx]?.otherNurseId ?? nurse.id;
		const reqData = request?.message.request_data.find((d) => d.nurse_id === nurseIdToCompare && d.status === "pending");

		let nurseToRemove: NurseInShift | undefined;
		const shiftTimes = request
			? { startTime: reqData?.accepted_start_time ?? request.message.shift_start, endTime: reqData?.accepted_end_time ?? request.message.shift_end }
			: { startTime: nurseToAdd.startTime, endTime: nurseToAdd.endTime };

		switch (displayedSuggestions[0].action) {
			case SuggestionAction.Replace:
				// Backward compatability support
				if (rec?.suggestions[idx].shiftId) {
					const weeklyShifts = schedule.weeks[weekIndex].weeklyShifts;

					nurseToRemove = weeklyShifts.reduce(
						(removedNurse, currentShift) => {
							const nurse = currentShift.days[dayNo].nurses.find((n) => n.id === rec?.suggestions[idx].shiftId);
							if (nurse) {
								return nurse;
							}
							return removedNurse;
						},
						undefined as NurseInShift | undefined,
					);
				} else {
					nurseToRemove = schedule.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo].nurses.find((n) => n.nurseId === rec?.suggestions[idx].nurseId);
				}

				if (!nurseToRemove) {
					showErrorSnackbar("Couldn't find the nurse who asked for this request", { request });
					return;
				}

				nurseToAdd = nurseShiftOf(rec?.suggestions[idx]?.otherNurseId as number, {
					...slotDay,
					location: nurseToRemove.location as string,
					requestId: request?.id,
					shiftTimes: {
						start: reqData?.accepted_start_time ?? nurseToRemove.startTime,
						end: reqData?.accepted_end_time ?? nurseToRemove.endTime,
						date: reqData?.accepted_start_time ?? nurseToRemove.startTime,
					},
					shiftBonus: bonus,
				});
				if (!nurseToAdd) {
					showErrorSnackbar(t("error.approve.request"), {
						nurse_id: rec?.suggestions[idx]?.otherNurseId,
						request_id: request?.id,
						reason: "nurse not on the department roster",
					});
					throw new Error(`Nurse ${rec?.suggestions[idx]?.otherNurseId} is not on the department roster`);
				}
				nurseToAdd.location = nurseToRemove?.location;

				try {
					await replaceRequest({
						nurseToAdd,
						nurseToRemove,
						requestId: request?.id,
						date: request ? request.message.date : slotDay.date,
					});
					showSnackbar(t("success.request.approved"), "success");
				} catch (e) {
					showErrorSnackbar(t("error.approve.request"), { nurse_id: nurseToAdd.id, request_id: request?.id }, e);
					throw e;
				}
				break;
			case SuggestionAction.Add:
				try {
					await addNurse({
						nurse: nurseToAdd,
						...slotDay,
						requestId: request?.id,
						shiftTimes,
						source: NurseInShiftCreationSource.Requests,
					});
					showSnackbar(
						t("nurse.added.success", {
							nurseName: nurseToAdd?.name,
							day: nurseToAdd?.startTime.toFormat("ccc"),
							shiftType: t(nurseToAdd?.shiftType),
						}),
						"success",
					);
				} catch (e: any) {
					let errorMessage = t("error.add.nurse");
					if (e.response?.data?.detail) {
						errorMessage = e.response.data.detail;
					}
					showErrorSnackbar(errorMessage, { nurseId: nurseToAdd.id }, e);
					throw new Error(errorMessage);
				}
				break;
			case SuggestionAction.Remove:
				nurseToRemove = schedule.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo].nurses.find((n) => n.nurseId === nurse.id) as NurseInShift;

				try {
					trackEvent("Delete nurse clicked", {
						nurse_id: nurse?.id,
						shift_id: nurseToRemove.id,
					});
					await removeNurse({ nurse: nurseToRemove, ...slotDay });
					showSnackbar(
						t("nurse.removed.success", {
							nurseName: nurseToRemove?.name,
							day: nurseToRemove?.startTime.toFormat("ccc"),
							shiftType: t(nurseToRemove?.shiftType),
						}),
						"success",
					);
				} catch (e) {
					showErrorSnackbar(t("error.remove.nurse"), { nurseId: nurseToRemove.id }, e);
					throw e;
				}
				break;
			case SuggestionAction.Move: {
				nurseToRemove = schedule.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo].nurses.find((n) => n.nurseId === nurse.id) as NurseInShift;
				const otherShift = schedule.weeks[weekIndex].weeklyShifts[displayedSuggestions[0].otherShiftNo!];
				const otherShiftTimes = getShiftTimes(otherShift.days[displayedSuggestions[0].otherDayNo!], displayedSuggestions[0].otherShiftNo!, nurse.id);
				const otherSlotDay = scheduleSlotDay(schedule.weeks, {
					weekIndex,
					shiftIndex: displayedSuggestions[0].otherShiftNo!,
					dayIndex: displayedSuggestions[0].otherDayNo!,
				});

				try {
					await moveNurse({
						nurse: nurseToRemove,
						...slotDay,
						otherDate: otherSlotDay.date,
						otherShiftType: otherSlotDay.shiftType,
						otherShiftTimes,
					});
					showSnackbar(
						t("nurse.moved.success", {
							nurseName: nurseToRemove?.name,
							fromDay: nurseToRemove?.startTime.toFormat("ccc"),
							fromShiftType: t(nurseToRemove?.shiftType),
							toDay: otherShiftTimes?.start?.toFormat("ccc"),
							toShiftType: t(schedule.weeks[weekIndex].weeklyShifts[displayedSuggestions[0].otherShiftNo!].type),
						}),
						"success",
					);
				} catch (e) {
					showErrorSnackbar(t("error.move.nurse"), { nurseId: nurseToRemove.id }, e);
					throw e;
				}

				break;
			}
		}
	};

	const handleAcceptSuggestion = async (idx: number) => {
		if (!validateScheduledDeactivationDate(idx)) {
			return;
		}

		if (isRequestedTab) {
			setPendingSuggestionIndex(idx);

			const nurse = schedule.nurses.find((n) => n.id === rec.suggestions[idx].nurseId);

			if (nurse) {
				const isAgency = isAgencyNurse(nurse.employmentType);

				const overtimeHours = 0;
				const overtimeCost = "$0";

				checkWarnings(isAgency, overtimeHours, overtimeCost);
				return;
			}
		}

		executeAcceptSuggestion(idx);
	};

	const executeAcceptSuggestion = async (idx: number) => {
		if (idx < 0) return;

		const requestDay = request && scheduleDayOf({ startTime: request.message.shift_start, shiftType: request.message.shift_type });
		const requestSlot = requestDay && findScheduleSlot(schedule.weeks, requestDay.date, requestDay.shiftType);
		if (request && !requestSlot) {
			showErrorSnackbar(t("error.approve.request"), { request_id: request.id, reason: "request day not loaded" });
			return;
		}
		const shiftNo = requestSlot ? requestSlot.shiftIndex : rec.suggestions[idx].shiftNo;
		const dayNo = requestSlot ? requestSlot.dayIndex : rec.suggestions[idx].dayNo;
		const weekIndex = requestSlot ? requestSlot.weekIndex : rec.suggestions[idx].weekIndex;

		if (isRequestedTab) {
			trackEvent("Staffing - request accepted", {
				source: "recommendation",
				suggestion_id: rec.notificationId,
				week_index: weekIndex,
				nurse_id: rec.suggestions[idx].nurseId,
				date: rec.shiftTimes?.start.toFormat("MM/DD/yyyy"),
				num_of_nurses: editingNeededNurses,
			});
		} else {
			trackEvent("Staffing - accept recommendation", {
				source: "recommendation",
				suggestion_id: rec.notificationId,
				nurse_id: rec.suggestions[idx].nurseId,
				date: rec.shiftTimes?.start.toFormat("MM/DD/yyyy"),
				num_of_nurses: editingNeededNurses,
			});
		}
		// in case of open shift, we need to validate that the nurse didn't cancel the picked shift before we approved the request
		// if (isRequestedTab) {
		//     if (!await validateNurseDidntCancelPickedShift(idx)) {
		//         return;
		//     }
		// }
		try {
			const nurse = schedule.nurses.find((nurse) => nurse.id === rec.suggestions[idx].nurseId);
			if (!nurse) {
				showErrorSnackbar(t("error.approve.request"), {
					nurse_id: rec.suggestions[idx].nurseId,
					request_id: request?.id,
					reason: "nurse not on the department roster",
				});
				return;
			}
			await approveSuggestionAsync(nurse, dayNo, shiftNo, idx, weekIndex);
		} catch {
			return;
		}

		generalHighlightNurseId(-1, "clearAll");

		const { date, shiftType } = scheduleSlotDay(schedule.weeks, { weekIndex, shiftIndex: shiftNo, dayIndex: dayNo });
		handleSuggestion(rec.suggestions[idx], date, shiftType, rec.shiftTimes);

		setIsApproved(true);
		onRemoveRecommendation(index, idx, false);

		const requestId = rec.suggestions[0]?.requestId;
		if (!isRequestedTab && requestId) {
			setStaffRequests((prevRequests) =>
				prevRequests.map((req) =>
					req.id === requestId
						? {
								...req,
								message: {
									...req.message,
									request_data: req.message.request_data.filter(
										(data) => !(data.status === RequestResponsesStatusOptions.PENDING && data.nurse_id === rec.suggestions[idx].otherNurseId),
									),
								},
							}
						: req,
				),
			);
		}
	};

	const handleMouseLeave = () => {
		if (rec.suggestions && rec.suggestions.length > 0) {
			const suggestion = rec.suggestions[0];
			generalHighlightShift(suggestion.shiftNo, suggestion.dayNo, "clear");
		}
	};

	const { title, subtitle } = useMemo(() => getRecommendationTitle(rec, schedule, isAutoScheduler), [rec, schedule, isAutoScheduler]);

	const displayedSuggestions = useMemo(() => {
		return isExpanded ? rec.suggestions : rec.suggestions.slice(0, 1);
	}, [isExpanded, rec.suggestions]);

	const shiftTimeDisplay = useMemo(() => {
		if (rec.shiftTimes) {
			return {
				date: rec.shiftTimes.date.toFormat("ccc, MMM d"),
				time: `${rec.shiftTimes.start.toFormat("HH:mm")}-${rec.shiftTimes.end.toFormat("HH:mm")}`,
			};
		}
		if (isAutoScheduler) {
			return {
				date: startingDate.plus({ days: weekIndex * 7 + (rec.suggestions[0]?.otherDayNo || 0) }).toFormat("ccc, MMM d"),
				time: rec.suggestions[0]?.otherShiftNo === 0 ? "06:00-18:30" : "18:30-06:00",
			};
		}
		return {
			date: startingDate.plus({ days: weekIndex * 7 + (rec.suggestions[0]?.dayNo || 0) }).toFormat("ccc, MMM d"),
			time: rec.suggestions[0]?.shiftNo === 0 ? "06:00-18:30" : "18:30-06:00",
		};
	}, [rec.shiftTimes, startingDate, weekIndex, rec.suggestions]);

	const handleOpenMatrix = () => {
		setIsMatrixOpen(true);
	};

	const handleCloseMatrix = () => {
		setIsMatrixOpen(false);
	};

	const handleOpenApprovalDialog = () => {
		setIsRequestApprovalDialogOpen(true);
	};

	const handleCloseApprovalDialog = () => {
		setIsRequestApprovalDialogOpen(false);
	};

	const handleOpenRemoveApprovalDialog = () => {
		setIsRemoveApprovalDialogOpen(true);
	};

	const handleOpenAddApprovalDialog = () => {
		setIsAddApprovalDialogOpen(true);
	};

	const handleOpenReplaceApprovalDialog = () => {
		setIsReplaceApprovalDialogOpen(true);
	};

	const handleCloseRemoveApprovalDialog = () => {
		setIsRemoveApprovalDialogOpen(false);
	};

	const handleCloseAddApprovalDialog = () => {
		setIsAddApprovalDialogOpen(false);
	};

	const handleCloseReplaceApprovalDialog = () => {
		setIsReplaceApprovalDialogOpen(false);
	};

	return (
		<>
			<Box
				onMouseEnter={() => {
					if (rec.suggestions && rec.suggestions.length > 0) {
						const suggestion = rec.suggestions[0];
						if (suggestion.weekIndex !== weekIndex) return;

						const dayNo = isAutoScheduler ? (suggestion.otherDayNo as number) : suggestion.dayNo;
						generalHighlightShift(suggestion.shiftNo, dayNo, "highlight");
					}
				}}
				onMouseLeave={() => {
					if (rec.suggestions && rec.suggestions.length > 0) {
						const suggestion = rec.suggestions[0];
						if (suggestion.weekIndex !== weekIndex) return;
						handleMouseLeave();
					}
				}}
				key={index}
				sx={{
					boxShadow: "0px",
					borderRadius: "8px",
					my: 2,
					py: 1,
					px: 1.5,
					width: "100%",
					zIndex: 999,
					color: "primary.dark",
					display: "block",
					border: "1px solid #E3E4F0",
					position: "relative",
				}}
			>
				{showBroadcastMessagePopup && (
					<NewBroadcastMessageModal
						initialSmsMessage={getShiftPickupMessage(rec.shiftTimes?.date, rec.shiftTimes?.start, rec.shiftTimes?.end)}
						addAppLink={true}
						defaultWorkingFilter={MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK}
						nurseIdsToIgnore={rec.suggestions.flatMap((s) => (s.otherNurseId ? [s.nurseId, s.otherNurseId] : [s.nurseId]))}
						jobId={request?.job_id}
						initialRolesToSelect={relevantRoles?.map((r) => NurseFullRole[r as keyof typeof NurseFullRole])}
						onClose={toggleShowBroadcastMessagePopup}
						initialFilters={
							rec.shiftTimes
								? [
										{
											workFilter: MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK,
											dateFilter: rec.shiftTimes.date,
											timeFilter: {
												start: rec.shiftTimes.start,
												end: rec.shiftTimes.end,
											},
										},
									]
								: undefined
						}
					/>
				)}
				<Box sx={{ pl: 0 }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 2,
						}}
					>
						{!isRequestedTab && (
							<Box
								sx={{
									width: "12px",
									height: "12px",
									borderRadius: "50%",
									backgroundColor:
										rec.priority === IssuePriority.TopPriority ? SEVERITY.HIGH : rec.priority === IssuePriority.Important ? SEVERITY.MEDIUM : "primary.main",
									flexShrink: 0,
								}}
							/>
						)}
						{isRequestedTab ? (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									width: "100%",
									justifyContent: "space-between",
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										color: "primary.dark",
									}}
								>
									{requestType === NotificationType.ReplaceShift ? (
										<>
											<SwapHorizIcon sx={{ fontSize: 20 }} />
											<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
												Replace Request
											</Typography>
											<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
												{rec.title[3]?.text || ""}
											</Typography>
										</>
									) : (
										<>
											<East sx={{ fontSize: 20 }} />
											<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
												Open Shift
											</Typography>
											<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
												{rec.title[3]?.text || ""}
											</Typography>
										</>
									)}
								</Box>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									{requestType === NotificationType.ReplaceShift ? (
										<Typography
											variant="subtitle2"
											title={rec.title[0]?.text || ""}
											sx={{
												fontWeight: 600,
												maxWidth: "80px",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{rec.title[0]?.text || ""}
										</Typography>
									) : (
										<Typography
											title={title}
											variant="subtitle2"
											sx={{
												fontWeight: 600,
												maxWidth: "80px",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{title}
										</Typography>
									)}
									{isRequestedTab && (
										<Fragment>
											<InHouseTooltip title={t("message.relevant.nurses")}>
												<IconButton size={"small"} onClick={toggleShowBroadcastMessagePopup}>
													<ChatOutlinedIcon fontSize={"small"} />
												</IconButton>
											</InHouseTooltip>
											<IconButton
												onClick={async () => {
													if (requestType === NotificationType.ReplaceShift) {
														onDeleteReplacementRequest();
													} else {
														await handleDelete();
													}
												}}
												disabled={isDeleting}
												sx={{
													padding: "4px",
													"&:hover": {
														backgroundColor: "rgba(238, 95, 75, 0.1)",
													},
													position: "relative",
												}}
											>
												{isDeleting ? (
													<CircularProgress
														size={20}
														sx={{
															color: "primary.main",
															position: "absolute",
															top: "50%",
															left: "50%",
															marginTop: "-10px",
															marginLeft: "-10px",
														}}
													/>
												) : (
													<DeleteOutlined sx={{ fontSize: 20 }} />
												)}
											</IconButton>
										</Fragment>
									)}
								</Box>
							</Box>
						) : (
							<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
								{title}
							</Typography>
						)}
					</Box>

					{assignment && (
						<Box>
							<Box sx={{ display: "flex", justifyContent: "space-between" }}>
								<Box sx={{ display: "flex", alignItems: "center" }}>
									<span
										style={{
											width: 8,
											height: 8,
											borderRadius: "50%",
											backgroundColor: freeLabelColor(assignment).dot,
										}}
									/>
									<Typography
										sx={{
											fontWeight: 500,
											fontSize: "14px",
											color: (theme) => alpha(theme.palette.primary.dark, 0.5),
											opacity: "50%",
											ml: 1,
										}}
									>
										{assignment}
									</Typography>
								</Box>
							</Box>
						</Box>
					)}
					{isRequestedTab && request && (
						<Box>
							{(() => {
								const labels = [];
								if (request.charge_only) {
									labels.push("Charge Only");
								}
								// future use
								// if (request.allow_overtime) {
								// 	labels.push("Allow Overtime");
								// }
								// if (request.allow_agency) {
								// 	labels.push("Allow Agency");
								// }
								return (
									<Box sx={{ display: "flex", gap: 2, ml: 1 }}>
										{labels.map((label) => (
											<Typography
												key={label}
												sx={{
													fontSize: 14,
													color: "primary.dark",
												}}
											>
												• {label}
											</Typography>
										))}
									</Box>
								);
							})()}
						</Box>
					)}

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							mt: 1,
							color: (theme) => alpha(theme.palette.primary.dark, 0.5),
							fontSize: "14px",
							fontWeight: 600,
							borderBottom: "1px solid #E3E4F0",
							pb: 1,
							whiteSpace: "nowrap",
							overflow: "hidden",
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								flexShrink: 0,
							}}
						>
							<React.Fragment key="shift-time">
								{shiftTimeDisplay.date}
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<Box
										component="span"
										sx={{
											width: "16px",
											height: "16px",
											display: "inline-flex",
											alignItems: "center",
											justifyContent: "center",
											flexShrink: 0,
										}}
									>
										{(() => {
											return (isRequestedTab && rec.shiftTimes && rec.shiftTimes.start.hour < 18) ||
												(!isRequestedTab && (rec.suggestions[0]?.shiftNo === 0 || rec.shiftTimes?.shiftType === ShiftType.Day)) ? (
												<Box
													key="day-icon"
													component="img"
													src="/sun.svg"
													alt="Day shift"
													sx={{
														width: 16,
													}}
												/>
											) : (
												<Box
													key="night-icon"
													component="img"
													src="/moon.svg"
													alt="Night shift"
													sx={{
														width: 16,
													}}
												/>
											);
										})()}
									</Box>
									{isRequestedTab && (
										<Typography
											component="span"
											sx={{
												color: (theme) => alpha(theme.palette.primary.dark, 0.5),
												fontSize: "14px",
												fontWeight: 600,
											}}
										>
											{shiftTimeDisplay.time
												.split("-")
												.map((time) => DateTime.fromFormat(time, "HH:mm").toFormat("h:mm a"))
												.join("-")}
										</Typography>
									)}
								</Box>
							</React.Fragment>
						</Box>
						{!isRequestedTab && (
							<Typography
								component="span"
								sx={{
									color: (theme) => alpha(theme.palette.primary.dark, 0.5),
									fontSize: "14px",
									fontWeight: 600,
									ml: 1,
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								{subtitle}
							</Typography>
						)}
					</Box>
					{!!bonus && (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1 }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.5,
									bgcolor: "var(--inhouse-secondary)",
									borderRadius: "16px",
									px: 1.5,
									alignSelf: "center",
								}}
							>
								<img src="/bonus.svg" alt="bonus" width={12} />
								<Typography
									sx={{
										color: "white",
										fontWeight: "bold",
										fontSize: "0.875rem",
									}}
								>
									+${bonus} pickup bonus
								</Typography>
							</Box>
						</Box>
					)}

					{!isTitleOnly && (
						<>
							<Grid container direction="row" sx={{ marginTop: 1, justifyContent: "space-between", alignItems: "center", flexWrap: "nowrap" }}>
								<Typography
									variant="body2"
									sx={{
										fontSize: 12,
										color: "primary.dark",
										fontWeight: 600,
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}
								>
									{isRequestedTab ? (
										<>
											{rec.title[4]?.text}
											{requestType === NotificationType.OpenShift && (
												<Edit
													sx={{
														fontSize: 16,
														color: "primary.dark",
														cursor: "pointer",
													}}
													onClick={(event: React.MouseEvent<SVGSVGElement>) => handleEditClick(event as any)}
												/>
											)}
										</>
									) : (
										(() => {
											const noResponse = rec.title
												.map((t) => t.text)
												.join(" ")
												.includes("without response yet");
											const postNewOpenShift = rec.title
												.map((t) => t.text)
												.join(" ")
												.includes("post new open shift");
											return rec.suggestions.length > 0
												? noResponse
													? t("no.response.yet")
													: postNewOpenShift
														? t("post.new.open.shift")
														: `${t("suggestions")} (${rec.suggestions.length})`
												: "";
										})()
									)}
								</Typography>
								{isRequestedTab && rec.suggestions.length > 0 && (
									<Typography
										variant="body2"
										sx={{
											fontSize: 12,
											textDecoration: "underline",
											cursor: "pointer",
											color: "primary.main",
											marginLeft: "auto",
										}}
										onClick={handleOpenApprovalDialog}
									>
										Show all
									</Typography>
								)}
								{(rec.suggestions.length >= 1 || chosenDepartment.department_id === DepartmentNames.AutoScheduler) && !isRequestedTab && (
									<Typography
										variant="body2"
										sx={{
											fontSize: 12,
											textDecoration: "underline",
											cursor: "pointer",
											color: "primary.main",
										}}
										onClick={() => {
											if (chosenDepartment.department_id === DepartmentNames.AutoScheduler) {
												handleOpenMatrix();
											} else if (rec.suggestions[0].action === SuggestionAction.Remove) {
												handleOpenRemoveApprovalDialog();
											} else if (rec.suggestions[0].action === SuggestionAction.Add) {
												handleOpenAddApprovalDialog();
											} else if (rec.suggestions[0].action === SuggestionAction.Replace) {
												handleOpenReplaceApprovalDialog();
											} else {
												setIsExpanded(!isExpanded);
											}
										}}
									>
										{isExpanded ? "Show less" : "Show all"}
									</Typography>
								)}
							</Grid>

							{isMatrixOpen && (
								<ErrorBoundary>
									<MoveNursePanel
										open={isMatrixOpen}
										onClose={handleCloseMatrix}
										{...scheduleSlotDay(schedule.weeks, {
											weekIndex,
											// The matrix only opens for the auto-scheduler's move suggestions, which always carry the other slot.
											shiftIndex: rec.suggestions[0].otherShiftNo as number,
											dayIndex: rec.suggestions[0].otherDayNo as number,
										})}
									/>
								</ErrorBoundary>
							)}

							<Menu
								anchorEl={anchorEl}
								open={Boolean(anchorEl)}
								onClose={handleClose}
								anchorOrigin={{
									vertical: "bottom",
									horizontal: "left",
								}}
								transformOrigin={{
									vertical: "top",
									horizontal: "left",
								}}
								sx={{
									"& .MuiPaper-root": {
										p: 2,
									},
								}}
							>
								<TextField
									type="number"
									value={editingNeededNurses}
									label="Nurses needed"
									onChange={(e) => {
										const value = Number.parseInt(e.target.value);
										if (!Number.isNaN(value) && value > 0) {
											setEditingNeededNurses(value);
										}
									}}
									size="small"
									sx={{ width: "100%" }}
									slotProps={{
										htmlInput: { min: 1 },
									}}
								/>
								<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
									<HeaderButton text="Save" onPress={handleSave} state="on" disabled={false} width="100%" />
								</Box>
							</Menu>
							{request != null && (
								<NormalizedRequestApprovalDialog
									isRecommendation={true}
									open={isRequestApprovalDialogOpen}
									request={request}
									onClose={handleCloseApprovalDialog}
								/>
							)}
							{isRemoveApprovalDialogOpen && (
								<ErrorBoundary>
									<NurseRemovalDialog
										open={isRemoveApprovalDialogOpen}
										onClose={handleCloseRemoveApprovalDialog}
										issue={rec}
										date={
											scheduleSlotDay(schedule.weeks, {
												weekIndex: rec.suggestions[0].weekIndex,
												shiftIndex: rec.suggestions[0].shiftNo,
												dayIndex: rec.suggestions[0].dayNo,
											}).date
										}
									/>
								</ErrorBoundary>
							)}
							{displayedSuggestions.map((suggestion: Suggestion, idx: number) => {
								const changeKey = `${rec.title}-${suggestion.action}-${idx}`;
								const nurseIdToCompare = suggestion.otherNurseId ?? suggestion.nurseId;
								const isImplemented = implementedChanges[changeKey];

								let acceptedStart: DateTime | undefined;
								let acceptedEnd: DateTime | undefined;
								if (isRequestedTab && request && request.message && request.message.request_data) {
									const reqData = request.message.request_data.find((d) => d.nurse_id === nurseIdToCompare && d.status === "pending");
									if (reqData?.accepted_start_time && reqData?.accepted_end_time) {
										acceptedStart = reqData.accepted_start_time;
										acceptedEnd = reqData.accepted_end_time;
									}
								}

								return (
									<Box
										key={idx}
										sx={{
											width: "100%",
											backgroundColor: "var(--inhouse-secondary-100)",
											borderRadius: "8px",
											m: 0,
											mt: 1,
											px: 0.7,
											py: 1,
											fontWeight: 600,
										}}
									>
										<Grid container direction="row" sx={{ justifyContent: "flex-start", alignItems: "center", flexWrap: "nowrap" }}>
											<Grid
												container
												direction="row"
												sx={{
													maxWidth: rec.issueType === "Shift" ? "200px" : "110px",
													width: "fit-content",
													justifyContent: "flex-start",
													alignItems: "center",
													flexWrap: "nowrap",
												}}
											>
												{rec.issueType === "Shift" || suggestion.displayNurse ? (
													<Box
														sx={{
															display: "flex",
															flexDirection: "column",
															alignItems: "center",
															width: "100%",
															paddingLeft: 0,
														}}
													>
														<Box
															sx={{
																display: "flex",
																alignItems: "center",
																width: "90%",
																gap: 1,
																marginRight: "5px",
															}}
														>
															<Avatar
																src={imageUrls?.[suggestion.nurseId] ? imageUrls[suggestion.nurseId] : "/default-profile.png"}
																sx={{ width: 24, height: 24 }}
															/>
															<Typography
																sx={{
																	fontSize: 12,
																	flex: "1 0 90%",
																	fontWeight: 600,
																	textOverflow: "ellipsis",
																	whiteSpace: "nowrap",
																	overflow: "hidden",
																}}
															>
																{suggestion.customDisplayText || getNurseFormattedName(suggestion.nurseId)}
															</Typography>
														</Box>
													</Box>
												) : (
													<>
														<CalendarIcon sx={{ pl: 0, ml: 0 }} />
														<Typography
															sx={{
																fontSize: 12,
																marginLeft: "5px",
																width: 110,
																fontWeight: 600,
															}}
															noWrap
														>
															{shiftTimeDisplay.date}
															{suggestion.shiftNo === 0 ? "Morning" : "Night"}
														</Typography>
													</>
												)}
											</Grid>
											{suggestion.action === SuggestionAction.Swap && (
												<Grid container direction="row" sx={{ justifyContent: "flex-start", alignItems: "center", flexWrap: "nowrap" }}>
													<SwapHorizIcon sx={{ mr: 1, pl: 0, ml: 0 }} />
													{suggestion.otherNurseId && (
														<Avatar
															src={imageUrls?.[suggestion.otherNurseId] ? imageUrls[suggestion.otherNurseId] : "/default-profile.png"}
															sx={{
																width: 24,
																height: 24,
																marginRight: "8px",
															}}
														/>
													)}
													<Typography sx={{ fontSize: 12, fontWeight: 600 }}>{getNurseFormattedName(suggestion.otherNurseId as number)}</Typography>
												</Grid>
											)}
											{suggestion.action === SuggestionAction.Replace && (
												<Grid container direction="row" sx={{ justifyContent: "flex-start", alignItems: "center", flexWrap: "nowrap" }}>
													<ChangeCircleOutlined sx={{ mr: 1, pl: 0, ml: 0 }} />

													{suggestion.otherNurseId && (
														<Avatar
															src={imageUrls?.[suggestion.otherNurseId] ? imageUrls[suggestion.otherNurseId] : "/default-profile.png"}
															sx={{
																width: 24,
																height: 24,
																marginRight: "8px",
															}}
														/>
													)}
													<Typography sx={{ fontSize: 12, fontWeight: 600 }}>{getNurseFormattedName(suggestion.otherNurseId as number)}</Typography>
													{suggestion.isAccepted && (
														<Typography
															component="span"
															sx={{
																color: "green",
																fontSize: "11px",
																marginLeft: "8px",
															}}
														>
															accepted
														</Typography>
													)}
												</Grid>
											)}
											{suggestion.action === SuggestionAction.Remove && (
												<Grid container direction="row" sx={{ justifyContent: "flex-start", alignItems: "center", flexWrap: "nowrap" }}>
													<Backspace sx={{ mr: 1, pl: 0, ml: 0 }} />
													<Typography sx={{ fontSize: 12, fontWeight: 600 }} noWrap>
														Shift Removal
													</Typography>
												</Grid>
											)}
											{/* {suggestion.action === SuggestionAction.Replace && (
                                        <Grid container direction="row" justifyContent="flex-start" alignItems="center" wrap="nowrap">
                                            <Backspace sx={{ mr: 1, pl: 0, ml: 0 }} />
                                            <Typography sx={{ fontSize: 12,fontWeight: 600 }} noWrap>
                                                Shift Replacement
                                            </Typography>
                                        </Grid>
                                    )}                                     */}
											{suggestion.action === SuggestionAction.Move && (
												<Grid container direction="row" sx={{ justifyContent: "flex-start", alignItems: "center", flexWrap: "nowrap" }}>
													<Backspace sx={{ mr: 1, pl: 0, ml: 0 }} />
													<Typography sx={{ fontSize: 12, fontWeight: 600 }} noWrap>
														Shift Move
													</Typography>
												</Grid>
											)}
											{suggestion.action === SuggestionAction.Add && (
												<Grid container direction="row" sx={{ justifyContent: "flex-start", alignItems: "center", flexWrap: "nowrap" }}>
													<East sx={{ mr: 1, pl: 0, ml: 0 }} />
													<Typography sx={{ fontSize: 12, fontWeight: 600 }} noWrap>
														Shift Addition
													</Typography>
												</Grid>
											)}
											<Tooltip
												arrow
												enterTouchDelay={0}
												leaveTouchDelay={1500}
												sx={{ zIndex: 9999999999 }}
												title={isImplemented ? "Suggestion implemented" : "Approve suggestion"}
											>
												<Box
													component="img"
													src="/approve.svg"
													alt="Approve"
													sx={{
														width: 18,
														height: 18,
														cursor: isLoadingRequest ? "not-allowed" : "pointer",
														opacity: isLoadingRequest ? 0.5 : 1,
														filter: isLoadingRequest ? "grayscale(100%)" : "none",
													}}
													onClick={() => !isLoadingRequest && handleAcceptSuggestion(idx)}
												/>
											</Tooltip>
										</Grid>
										{acceptedStart && acceptedEnd && (
											<Typography sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
												*partial shift - {acceptedStart.toFormat("h:mm a")}-{acceptedEnd.toFormat("h:mm a")}{" "}
												{acceptedEnd.diff(acceptedStart, "hour").as("hours")}h
											</Typography>
										)}
									</Box>
								);
							})}
						</>
					)}
				</Box>
				{isAddApprovalDialogOpen && (
					<ErrorBoundary>
						<Dialog
							open={isAddApprovalDialogOpen}
							onClose={handleCloseAddApprovalDialog}
							maxWidth="md"
							fullWidth
							sx={{
								"& .MuiDialog-paper": {
									width: "90%",
									maxWidth: "1000px",
									maxHeight: "90vh",
									m: 0,
									p: 0,
									borderRadius: "8px",
									overflowY: "auto",
								},
							}}
						>
							<DialogContent sx={{ p: 0 }}>
								<AddNurse
									setIsAdd={() => {}}
									{...scheduleSlotDay(schedule.weeks, {
										weekIndex,
										shiftIndex: rec.suggestions[0].shiftNo,
										dayIndex: rec.suggestions[0].dayNo,
									})}
									rolesToDisplay={departmentNurseTypes || []}
									onNurseSelected={(nurse, selectedDate) => {
										const suggestion = {
											...rec.suggestions[0],
											nurseId: nurse.id,
											customDisplayText: getNurseFormattedName(nurse.id),
										};

										const { date, shiftType } = scheduleSlotDay(schedule.weeks, {
											weekIndex,
											shiftIndex: suggestion.shiftNo,
											dayIndex: suggestion.dayNo,
										});
										handleSuggestion(suggestion, date, shiftType, rec.shiftTimes);

										setIsApproved(true);
										onRemoveRecommendation(index, 0, false, nurse.id);
										handleCloseAddApprovalDialog();
									}}
									onClose={handleCloseAddApprovalDialog}
									setOpen={setIsAddApprovalDialogOpen}
									// The recommendation's callback takes exactly one nurse.
									selectionMode="single"
								/>
							</DialogContent>
						</Dialog>
					</ErrorBoundary>
				)}

				<WarningDialog
					openAgencyWarning={warningState.isAgencyWarningOpen}
					openOvertimeWarning={warningState.isOvertimeWarningOpen}
					openDailyHoursWarning={false}
					overtimeDetails={warningState.overtimeDetails}
					dailyHoursDetails={{ hours: 0 }}
					longShiftDuration={chosenDepartment.longShiftDuration}
					onCancelAgency={handleWarningCancel}
					onConfirmAgency={handleAgencyConfirm}
					onCancelOvertime={handleWarningCancel}
					onConfirmOvertime={handleOvertimeConfirm}
					onCancelDailyHours={handleWarningCancel}
					onConfirmDailyHours={handleOvertimeConfirm}
				/>
				{/* TODO - replace with the ReplaceNurse component */}
				{/* {isReplaceApprovalDialogOpen && (
                <Dialog
                    open={isReplaceApprovalDialogOpen}
                    onClose={handleCloseReplaceApprovalDialog}
                    maxWidth="md"
                    fullWidth
                    sx={{
                        '& .MuiDialog-paper': {
                            width: '90%',
                            maxWidth: '1000px',
                            maxHeight: '90vh',
                            m: 0,
                            p: 0,
                            borderRadius: '8px',
                            overflowY: 'auto'
                        }
                    }}
                >
                        {/*<DialogContent sx={{ p: 0 }}>

                          <ReplaceNurse
                            setIsReplace={() => {}}
                            dayNo={rec.suggestions[0]?.dayNo || 0}
                            shiftNo={rec.suggestions[0]?.shiftNo || 0}
                            rolesToDisplay={departmentNurseTypes || []}
                            onNurseSelected={(nurse, selectedDate) => {
                                const suggestion = {
                                    ...rec.suggestions[0],
                                    nurseId: nurse.id,
                                    customDisplayText: getNurseFormattedName(nurse.id)
                                };

                                // Handle the suggestion with the nurse
                                handleSuggestion(
                                    suggestion,
                                    weekIndex,
                                    suggestion.shiftNo,
                                    suggestion.dayNo,
                                    rec.shiftTimes
                                );

                                setIsApproved(true);
                                onRemoveRecommendation(index, 0, false, nurse.id);
                                handleCloseReplaceApprovalDialog();
                            }}
                            onClose={handleCloseReplaceApprovalDialog}
                            setOpen={setIsReplaceApprovalDialogOpen}
                        />
                    </DialogContent>
                    }
                {/* </Dialog> */}
				{/* )} */}
			</Box>
			<Dialog open={deleteRequestConfirmation} onClose={() => setDeleteRequestConfirmation(false)}>
				<DialogTitle>{t("are.you.sure")}</DialogTitle>
				<DialogContent>{t("delete.replace.warning")}</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteRequestConfirmation(false)}>{t("cancel")}</Button>
					<Button onClick={handleDelete}>{t("delete.request")}</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
